import React, { useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ClipboardList, Upload, FileWarning, PenLine, CalendarClock, School, Wand2 } from "lucide-react";
import { toast } from "sonner";

/**
 * 6 pre-built task templates for common workflows. Frontend-only definitions;
 * they call the Immigration tasks endpoint (which powers `My Tasks`).
 */
export const TASK_TEMPLATES = [
  { id: "ircc_upload", label: "Upload Missing Docs to IRCC", title: "Upload missing documents to IRCC portal", priority: "High", due_in_days: 3, icon: Upload, description: "IRCC has requested additional documentation. Upload before the portal deadline." },
  { id: "docs_missing", label: "Client Docs Missing", title: "Follow up with client — missing documents", priority: "High", due_in_days: 2, icon: FileWarning, description: "Reach out to the client to collect pending required documents." },
  { id: "retainer_followup", label: "Retainer Signing Follow-up", title: "Follow up on retainer agreement signing", priority: "Normal", due_in_days: 2, icon: PenLine, description: "Nudge the client to sign the retainer sent via DocuSign / email." },
  { id: "expiry_renewal", label: "Status Expiry Renewal Reminder", title: "Prepare renewal package before status expiry", priority: "Urgent", due_in_days: 21, icon: CalendarClock, description: "Client's current status expires soon. Kick off the renewal file." },
  { id: "pal_pending", label: "PAL Pending", title: "Provincial Attestation Letter (PAL) pending", priority: "High", due_in_days: 5, icon: School, description: "PAL required from provincial authority before study permit filing." },
  { id: "loa_followup", label: "LOA / Offer Letter Follow-up", title: "Follow up with college for LOA / Offer Letter", priority: "Normal", due_in_days: 4, icon: ClipboardList, description: "Chase the college's admissions office for the offer letter or LOA." },
];

/**
 * Renders a "New Task from Template" button + popover picker.
 * On pick → opens a prefilled task dialog → creates via /immigration endpoint.
 *
 * Props:
 *  - applicationId: string (immigration app id)
 *  - defaultAssignee: user_id (usually the file's assigned officer)
 *  - onCreated: () => void
 *  - meta: object with `officers` list (from immigration meta)
 */
export default function TaskTemplatePicker({ applicationId, defaultAssignee, onCreated, meta, testIdPrefix = "task-tpl" }) {
  const [open, setOpen] = useState(false); // popover
  const [form, setForm] = useState(null); // { title, priority, due_date, assigned_to, template_id }

  const openTemplate = (t) => {
    const due = new Date();
    due.setDate(due.getDate() + (t.due_in_days || 3));
    setForm({
      template_id: t.id,
      title: t.title,
      priority: t.priority,
      due_date: due.toISOString().slice(0, 10),
      assigned_to: defaultAssignee || "",
    });
    setOpen(false);
  };

  const create = async () => {
    if (!form?.title?.trim()) { toast.error("Title required"); return; }
    try {
      await api.post(`/immigration/applications/${applicationId}/tasks`, {
        title: form.title.trim(),
        priority: form.priority,
        due_date: form.due_date,
        assigned_to: form.assigned_to || null,
      });
      toast.success("Task created");
      setForm(null);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const officers = meta?.officers || meta?.users || [];
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button data-testid={`${testIdPrefix}-btn`} variant="outline" size="sm"><Wand2 className="w-4 h-4 mr-1" />New Task…</Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 bg-white p-2" align="end">
          <div className="text-xs uppercase tracking-widest text-slate-500 px-2 py-1">Quick templates</div>
          {TASK_TEMPLATES.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => openTemplate(t)} data-testid={`${testIdPrefix}-${t.id}`} className="w-full text-left px-2 py-2 rounded hover:bg-slate-50 flex items-start gap-2">
                <Icon className="w-4 h-4 text-orange-500 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-slate-900">{t.label}</div>
                  <div className="text-[11px] text-slate-500 leading-snug">{t.description}</div>
                </div>
                <span className="text-[9px] uppercase font-semibold px-1 py-0.5 rounded bg-slate-100 text-slate-600">{t.priority}</span>
              </button>
            );
          })}
        </PopoverContent>
      </Popover>

      <Dialog open={!!form} onOpenChange={(v) => { if (!v) setForm(null); }}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label className="text-xs">Title</Label>
                <Input data-testid="task-tpl-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                    <SelectTrigger data-testid="task-tpl-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Low", "Normal", "High", "Urgent"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Due date</Label>
                  <Input data-testid="task-tpl-due" type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} /></div>
              </div>
              <div><Label className="text-xs">Assign to</Label>
                <Select value={form.assigned_to || ""} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger data-testid="task-tpl-assignee"><SelectValue placeholder="Unassigned (default: file officer)" /></SelectTrigger>
                  <SelectContent>{officers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button data-testid="task-tpl-create" onClick={create} className="bg-orange-500 hover:bg-orange-600 text-white">Create task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
